import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, finalize, map, of, retry, shareReplay, tap } from 'rxjs';
import { MtmAuthBridgeService } from '@mtm/auth-ui-angular';
import { ApiService } from './api.service';
import { EmitterService } from './emitter.service';
import { User } from '../interfaces';
import { EffectivePermissions } from '../interfaces/auth-permissions.interface';
import { AllowedOperationsUpdatedEvent, UserLoggedOutEvent } from '../models/events';

const EFFECTIVE_PERMISSIONS_STORAGE_KEY: string = 'auth_effective_permissions';
const EFFECTIVE_PERMISSIONS_TTL_MS: number = 1 * 60 * 1000; // matches PARTICIPANTS_CACHE_TTL_MS

// The IAM registration endpoint still validates a RoleType `role` field even though role
// selection is gone from the UI; these are the app's last outbound role literals and die
// when the IAM contract drops the field.
export const INVITE_DEFAULT_ROLE: string = 'COMPANY_REGULAR';
export const ADMIN_INVITE_DEFAULT_ROLE: string = 'COMPANY_PRINCIPAL';

@Injectable({
  providedIn: 'root',
})
export class AuthPermissionsService {
  private readonly apiService: ApiService = inject(ApiService);
  private readonly bridge: MtmAuthBridgeService = inject(MtmAuthBridgeService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  private effectivePermissionsFetchedAt: number = 0;
  private effectivePermissionsInFlight$: Observable<EffectivePermissions> | null = null;
  private cachedRawPermissions: string | null = null;
  private cachedParsedPermissions: EffectivePermissions = {};

  constructor() {
    // Required for correctness, not just hygiene: logout() wipes localStorage, so a
    // still-"fresh" timestamp after re-login would serve {} from the cleared store.
    EmitterService.get(UserLoggedOutEvent)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.effectivePermissionsFetchedAt = 0;
        this.effectivePermissionsInFlight$ = null;
      });
  }

  /**
   * Effective permissions are account-wide, so per-project navigations reuse the last
   * fetch within a short TTL (and concurrent callers share one request). Pass force
   * where a permission change is expected (new token, project created).
   */
  public updateEffectivePermissions(force: boolean = false): Observable<EffectivePermissions> {
    // Effective permissions are tenant-scoped: a principal without a tenant (IAM login before
    // tenant selection, or an identity with none) produces an unscoped request the gateway
    // rejects, and retry(3) inside the login forkJoin turns that into a failed login. Skip
    // silently WITHOUT touching the TTL/in-flight state, so the next call re-requests as soon
    // as a tenant is present.
    if (!this.hasTenant()) {
      return of(this.getEffectivePermissions());
    }
    if (!force) {
      if (Date.now() - this.effectivePermissionsFetchedAt < EFFECTIVE_PERMISSIONS_TTL_MS) {
        return of(this.getEffectivePermissions());
      }
      if (this.effectivePermissionsInFlight$) {
        return this.effectivePermissionsInFlight$;
      }
    }
    const request$: Observable<EffectivePermissions> = this.apiService.httpGet('/api/permissions/effective').pipe(
      retry(3),
      map((response: EffectivePermissions) => this.storeEffectivePermissions(response)),
      tap(() => {
        this.effectivePermissionsFetchedAt = Date.now();
        EmitterService.get(AllowedOperationsUpdatedEvent).emit();
      }),
      finalize(() => (this.effectivePermissionsInFlight$ = null)),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.effectivePermissionsInFlight$ = request$;
    return request$;
  }

  // companyId carries the tenant: for IAM the tenant UUID is mapped onto it, for legacy users it is
  // the company. The SDK's in-memory tenant (and its localStorage copy) counts too, since it is set
  // on tenant resolution before the legacy user is hydrated.
  private hasTenant(): boolean {
    let companyId: string | undefined;
    try {
      const stored: string | null = localStorage.getItem('auth_user');
      companyId = stored ? (JSON.parse(stored) as User).companyId : undefined;
    } catch {
      companyId = undefined;
    }
    return !!(companyId || this.bridge.getTenantUuid() || localStorage.getItem('auth_tenant_uuid'));
  }

  // Parse is memoized by raw-string identity: any external change to the stored value
  // (another tab, logout's localStorage.clear + re-login) is picked up without hooks.
  public getEffectivePermissions(): EffectivePermissions {
    const stored: string | null = localStorage.getItem(EFFECTIVE_PERMISSIONS_STORAGE_KEY);
    if (stored === null) {
      return {};
    }
    if (stored !== this.cachedRawPermissions) {
      this.cachedRawPermissions = stored;
      this.cachedParsedPermissions = JSON.parse(stored);
    }
    return this.cachedParsedPermissions;
  }

  private storeEffectivePermissions(response: EffectivePermissions): EffectivePermissions {
    const permissions: EffectivePermissions = response ?? {};
    localStorage.setItem(EFFECTIVE_PERMISSIONS_STORAGE_KEY, JSON.stringify(permissions));
    // The pre-cutover letter aggregate; remove the orphan from continuing sessions.
    localStorage.removeItem('auth_user_allowed_operations');
    return permissions;
  }
}
